import React from 'react';
import { Loader2, CheckCircle, XCircle, Circle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface PlaygroundOutputProps {
  output: string | null;
  error: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed';
}

export function PlaygroundOutput({
  output,
  error,
  status,
}: PlaygroundOutputProps) {
  const getStatusIcon = () => {
    switch (status) {
      case 'running':
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <Circle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'running':
        return 'Running...';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return 'Ready';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Status indicator */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 rounded-t-lg">
        {getStatusIcon()}
        <span className="text-sm text-muted-foreground">{getStatusText()}</span>
      </div>

      {/* Output content */}
      <ScrollArea className="flex-1 rounded-b-lg border border-t-0 bg-muted/10">
        <div className="p-4">
          {status === 'idle' && !output && !error && (
            <p className="text-sm text-muted-foreground">
              Click "Run" to execute the agent and see the output here.
            </p>
          )}

          {status === 'running' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Executing agent...</span>
            </div>
          )}

          {error && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-destructive">Error:</p>
              <pre className="text-sm font-mono whitespace-pre-wrap text-destructive/80 bg-destructive/10 p-3 rounded">
                {error}
              </pre>
            </div>
          )}

          {output && !error && (
            <pre className="text-sm font-mono whitespace-pre-wrap">
              {output}
            </pre>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default PlaygroundOutput;
